import {
  AlertTriangle,
  Calendar,
  Eye,
  EyeOff,
  Hash,
  Pencil,
  SeparatorHorizontal,
  ToggleLeft,
  Type,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useIntl } from '@/core/i18n/use-intl';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui';
import { cn } from '@/shared/utils';
import type { CustomerAccess, FieldType, FieldWidth, MetadataField } from './types';

interface MetadataFieldDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  field?: MetadataField | null;
  onSave: (field: Partial<MetadataField>) => void;
  allowedCustomerAccess?: CustomerAccess[];
}

const fieldTypeOptions: {
  value: FieldType;
  icon: React.ComponentType<{ className?: string }>;
  descriptionKey: string;
}[] = [
  {
    value: 'string',
    icon: Type,
    descriptionKey: 'fieldTypeDescriptions.string',
  },
  {
    value: 'number',
    icon: Hash,
    descriptionKey: 'fieldTypeDescriptions.number',
  },
  {
    value: 'boolean',
    icon: ToggleLeft,
    descriptionKey: 'fieldTypeDescriptions.boolean',
  },
  {
    value: 'date',
    icon: Calendar,
    descriptionKey: 'fieldTypeDescriptions.date',
  },
  {
    value: 'section',
    icon: SeparatorHorizontal,
    descriptionKey: 'fieldTypeDescriptions.section',
  },
];

export function MetadataFieldDialog({
  open,
  onOpenChange,
  field,
  onSave,
  allowedCustomerAccess = ['none', 'read', 'read-and-write'],
}: MetadataFieldDialogProps) {
  const { t } = useIntl();
  const isEditing = !!field?.key && !field.key.startsWith('temp-');

  const [type, setType] = useState<FieldType>('string');
  const [labelDe, setLabelDe] = useState('');
  const [labelEn, setLabelEn] = useState('');
  const [customerAccess, setCustomerAccess] = useState<CustomerAccess>('read');
  const [width, setWidth] = useState<FieldWidth>('50');
  const [required, setRequired] = useState(false);

  // Reset form when field changes
  useEffect(() => {
    if (field) {
      setType(field.type);
      setLabelDe(field.labelDe);
      setLabelEn(field.labelEn);
      setCustomerAccess(field.customerAccess);
      setWidth(field.width);
      setRequired(field.required);
    } else {
      setType('string');
      setLabelDe('');
      setLabelEn('');
      setCustomerAccess('read');
      setWidth('50');
      setRequired(false);
    }
  }, [field, open]);

  const handleSave = () => {
    if (!labelDe.trim()) {
      return;
    }

    onSave({
      type,
      labelDe: labelDe.trim(),
      labelEn: labelEn.trim(),
      customerAccess,
      width: type === 'section' ? '100' : width,
      required: type === 'section' ? false : required,
    });

    onOpenChange(false);
  };

  const customerAccessOptions = [
    { value: 'none', label: t('metadata.customerAccess.none'), icon: EyeOff },
    { value: 'read', label: t('metadata.customerAccess.read'), icon: Eye },
    {
      value: 'read-and-write',
      label: t('metadata.customerAccess.readAndWrite'),
      icon: Pencil,
    },
  ].filter((opt) => allowedCustomerAccess.includes(opt.value as CustomerAccess));

  const widthOptions: { value: FieldWidth; label: string }[] = [
    { value: '33', label: '33%' },
    { value: '50', label: '50%' },
    { value: '100', label: '100%' },
  ];

  const isSection = type === 'section';

  // Get the icon for the current type (for readonly display)
  const CurrentTypeIcon = fieldTypeOptions.find((opt) => opt.value === type)?.icon || Type;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? t('metadata.builder.editField') : t('metadata.builder.addField')}
          </DialogTitle>
          <DialogDescription>
            {isSection
              ? t('metadata.builder.sectionDescription')
              : t('metadata.builder.fieldDescription')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Field Type - Readonly when editing, Card Select when creating */}
          {isEditing ? (
            <div className="space-y-3">
              <Label className="text-sm font-medium">{t('common.type')}</Label>
              <div className="flex items-center gap-3 p-3 rounded-lg border bg-muted/30">
                <CurrentTypeIcon className="size-5 text-muted-foreground" />
                <div>
                  <span className="font-medium">{t(`metadata.fieldTypes.${type}`)}</span>
                  <p className="text-xs text-muted-foreground">
                    {t('metadata.builder.typeCannotBeChanged')}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <Label className="text-sm font-medium">{t('common.type')}</Label>
              <div className="grid grid-cols-5 gap-2">
                {fieldTypeOptions.map((option) => {
                  const Icon = option.icon;
                  const isSelected = type === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setType(option.value)}
                      className={cn(
                        'flex flex-col items-center justify-center gap-2 p-3 rounded-lg border-2 transition-all',
                        isSelected
                          ? 'border-primary bg-primary/5 text-primary'
                          : 'border-border bg-background hover:border-primary/50 hover:bg-muted/50 text-muted-foreground hover:text-foreground'
                      )}
                    >
                      <Icon className="size-5" />
                      <span className="text-xs font-medium">
                        {t(`metadata.fieldTypes.${option.value}`)}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Labels */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="labelDe">
                {t('common.label')} (DE) <span className="text-destructive">*</span>
              </Label>
              <Input
                id="labelDe"
                value={labelDe}
                onChange={(e) => setLabelDe(e.target.value)}
                placeholder={isSection ? 'Abschnittstitel' : 'Bezeichnung'}
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="labelEn">{t('common.label')} (EN)</Label>
              <Input
                id="labelEn"
                value={labelEn}
                onChange={(e) => setLabelEn(e.target.value)}
                placeholder={isSection ? 'Section title' : 'Label'}
              />
            </div>
          </div>

          {/* Access Level & Width (not for sections) */}
          {!isSection && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('metadata.customerAccess.label')}</Label>
                <Select
                  value={customerAccess}
                  onValueChange={(v) => setCustomerAccess(v as CustomerAccess)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {customerAccessOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        <div className="flex items-center gap-2">
                          <opt.icon className="size-4" />
                          <span>{opt.label}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('metadata.builder.width')}</Label>
                <Select value={width} onValueChange={(v) => setWidth(v as FieldWidth)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {widthOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Required checkbox (not for sections or booleans) */}
          {!isSection && type !== 'boolean' && (
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="required"
                  checked={required}
                  onCheckedChange={(checked) => setRequired(checked === true)}
                />
                <Label htmlFor="required" className="cursor-pointer">
                  {t('metadata.builder.requiredField')}
                </Label>
              </div>
              {required && (
                <p className="text-xs text-amber-600 flex items-center gap-1.5 ml-6">
                  <AlertTriangle className="size-3.5" />
                  {t('metadata.builder.requiredWarning')}
                </p>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={!labelDe.trim()}>
            {isEditing ? t('common.save') : t('metadata.builder.createField')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
